import type { ReactNode } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { signTextClass, tonePillClass } from "@/lib/tone";
import { rangeLabel } from "@/lib/time-range";

import type { RollingWindow, SeriesPoint, Tile } from "../api";
import {
  bandTone,
  formatDeltaPoints,
  formatTileValue,
  formatUnits,
  formatUnitsDelta,
  readsNarrowerWindow,
  unitsDeltaForRange,
  type Grain,
  type Range,
} from "../format";
import { UnitsTrend } from "./UnitsTrend";

/**
 * The two headline figures, each with its own windowed chart directly under
 * it - one `Card`, two columns, never two separate chart cards (W7).
 *
 * They are deliberately shown SIDE BY SIDE rather than as one number: the growth
 * rate and the volume answer different questions, and a market can post a
 * spectacular YoY percentage off a handful of units. Meteoric paired them for
 * that reason and the pairing carries across.
 */
export function UnitsHeadline({
  tile,
  window,
  range,
  grain,
  growthPoints,
  unitsPoints,
}: {
  tile: Tile;
  window: RollingWindow;
  range: Range;
  grain: Grain;
  growthPoints: SeriesPoint[];
  unitsPoints: SeriesPoint[];
}) {
  const tone = bandTone(window.band);
  const delta = formatDeltaPoints(window.delta_vs_prior_period);
  const unitsDelta = unitsDeltaForRange(window.total, range);
  const unitsDeltaText = formatUnitsDelta(unitsDelta);
  // W2: a range the producer has no window for reads the widest one below it
  // (`windowKeyForRange`) - state that plainly rather than let a reader believe
  // the range changed the numbers below it. 6mo now has its own window, so this
  // is true for 12mo and All time only, and is derived from the payload rather
  // than a hardcoded range list so it self-corrects when 365 is computed.
  const usesFixedWindow = readsNarrowerWindow(tile, range);

  return (
    <Card>
      <CardContent className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-10">
        <UnitsColumn>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {tile.kpi_name}
          </p>
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="text-4xl font-semibold tabular-nums text-ks-ink">
              {formatTileValue(window.value, tile.unit)}
            </span>
            {/* W2: "unknown" is a real band the producer emits, not a gap this
                surface is allowed to paint a judgement onto - so it renders no
                pill at all rather than guessing a colour for it. */}
            {window.band !== "unknown" && (
              <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${tonePillClass(tone)}`}>
                {window.band}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            {/* An absent delta renders nothing at all. A "0.00" would claim the
 rate held steady, which is not the same as having no prior
 window to compare it against. */}
            {delta !== "" && (
              <span className={`font-medium tabular-nums ${signTextClass(window.delta_vs_prior_period)}`}>
                {delta} vs prior window
              </span>
            )}
            <span className="text-muted-foreground">
              {window.period ?? "Period pending"}
              {usesFixedWindow && <span className="text-muted-foreground"> (fixed 90-day window)</span>}
            </span>
          </div>
          <UnitsTrend points={growthPoints} grain={grain} metric="growth" />
        </UnitsColumn>

        <UnitsColumn>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Units sold</p>
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="text-4xl font-semibold tabular-nums text-ks-ink">
              {formatUnits(window.total?.units ?? null)}
            </span>
            {/* The comparison names itself (WoW / MoM / YoY) because which one it
 is depends on the selected window - an unlabelled arrow beside a
 7-day count reads as a year-on-year move. */}
            {unitsDeltaText !== "" && (
              <span className={`text-sm font-medium tabular-nums ${signTextClass(unitsDelta.pct)}`}>
                {unitsDeltaText}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="text-muted-foreground">{rangeLabel(range)}</span>
          </div>
          <UnitsTrend points={unitsPoints} grain={grain} metric="units" />
        </UnitsColumn>
      </CardContent>
    </Card>
  );
}

function UnitsColumn({ children }: { children: ReactNode }) {
  return <div className="flex min-w-0 flex-1 flex-col gap-3">{children}</div>;
}
